import React, { useEffect, useState, useMemo } from "react";
import {
  Box,
  Typography,
  Paper,
  Grid,
  IconButton,
  Button,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Chip,
  Collapse,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import CloseIcon from "@mui/icons-material/Close";
import ImageIcon from "@mui/icons-material/Image";
import { getAllMonthlyTargetsAction, getAllActivitiesAction, getAllMediaFilesAction, getAllSeasonsAction, getAllDistrictsAction, getAllMandalsAction } from "../../store/actions";

const PreActivityDetailsView = ({ open, onClose, row }) => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.only("xs"));

  const [expandedMonthId, setExpandedMonthId] = useState(null);
  const [openImageDialog, setOpenImageDialog] = useState(false);

  const { successData: monthlyTargetsData = {} } = useSelector(
    (state) => state.getAllMonthlyTargetsReducer || {}
  );
  const { successData: activitiesData = {} } = useSelector(
    (state) => state.getAllActivitiesReducer || {}
  );
  const { successData: mediaFilesData = {} } = useSelector(
    (state) => state.getAllMediaFilesReducer || {}
  );
  const { successData: seasons = {} } = useSelector((state) => state.getAllSeasonsReducer || {});
  const { successData: districtsData = {} } = useSelector((state) => state.getAllDistrictsReducer || {});
  const { successData: mandalsData = {} } = useSelector((state) => state.getAllMandalsReducer || {});

  const seasonList = seasons?.data?.seasons || [];
  const districtList = districtsData?.data?.districts || [];
  const mandalList = mandalsData?.data?.mandals || [];

  // Dispatch actions on open
  useEffect(() => {
    if (open && row) {
      dispatch(getAllMonthlyTargetsAction());
      dispatch(getAllActivitiesAction());
      dispatch(getAllSeasonsAction());
      dispatch(getAllDistrictsAction());
      dispatch(getAllMandalsAction());
    }
  }, [open, row, dispatch]);

  // Filter monthly targets by pre_target_id
  const filteredMonthlyTargets = useMemo(() => {
    if (!row || !monthlyTargetsData) return [];

    let monthlyTargetsList = monthlyTargetsData?.data || [];

    if (!Array.isArray(monthlyTargetsList)) {
      return [];
    }

    return monthlyTargetsList.filter(target => {
      const targetPreTargetId = target.pre_target_id || target.preTargetId;
      return targetPreTargetId === row.id;
    });
  }, [row, monthlyTargetsData]);

  if (!row) return null;

  // Filter activities by pre_target_id and pre_month_id
  const getActivitiesForMonth = (monthId) => {
    if (!activitiesData) return [];

    let activitiesList = activitiesData?.activities || activitiesData?.data?.activities || activitiesData?.data || [];

    if (!Array.isArray(activitiesList)) {
      return [];
    }

    return activitiesList.filter(activity => {
      const activityPreTargetId = activity.preTargetId || activity.pre_target_id;
      const activityPreMonthId = activity.preMonthId || activity.pre_month_id;
      return activityPreTargetId === row.id && activityPreMonthId === monthId;
    });
  };

  // Open image dialog
  const handleOpenImages = (activityId) => {
    setOpenImageDialog(true);
    dispatch(getAllMediaFilesAction({
      endPoint: `activity-media/pre?activityId=${activityId}`
    }));
  };

  // Close image dialog
  const handleCloseImages = () => {
    setOpenImageDialog(false);
  };

  const handleToggleActivities = (monthId) => {
    setExpandedMonthId(expandedMonthId === monthId ? null : monthId);
  };

  // Get season display name
  const seasonObj = seasonList.find(s => s.seasonId === row?.season_id);
  const seasonDisplay = seasonObj ? `${seasonObj.name} (${seasonObj.year})` : row?.season_id;

  // Get area name based on area type
  const getAreaName = () => {
    if (!row) return '';
    if (row.target_area_type === 'mandal') {
      const mandal = mandalList.find(m => m.mandalId === row.target_area_id || m.id === row.target_area_id);
      const district = districtList.find(d => d.districtId === mandal?.districtId || d.id === mandal?.districtId);
      return district && mandal ? `${district.name} - ${mandal.name}` : mandal ? mandal.name : row.target_area_id;
    } else if (row.target_area_type === 'district') {
      const district = districtList.find(d => d.districtId === row.target_area_id || d.id === row.target_area_id);
      return district ? district.name : row.target_area_id;
    }
    return row.target_area_id;
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Typography variant="h6" fontWeight={600}>
          Pre-Season Activity Details
        </Typography>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ maxHeight: "calc(100vh - 200px)", overflowY: "auto" }}>
        {/* Pre-Season Activity Details Card */}
        <Paper elevation={0} sx={{ p: 2, mb: 3, borderRadius: 2, border: "1px solid #E5E7EB", position: "relative" }}>
          {isXs ? (
            <Box sx={{ p: 1, minWidth: 220, minHeight: 180, position: "relative" }}>
              <Chip
                label={row?.is_active ? "Active" : "Inactive"}
                size="small"
                sx={{
                  position: "absolute",
                  top: 12,
                  right: 12,
                  backgroundColor: row?.is_active ? '#D1FAE5' : '#FECACA',
                  color: row?.is_active ? '#047857' : '#B91C1C',
                  fontWeight: 600,
                  fontSize: 12,
                }}
              />
              <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, mt: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  <span style={{ color: "#374151" }}>Activity ID:</span> {row?.id}
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  <span style={{ color: "#374151" }}>Season:</span> {seasonDisplay}
                </Typography>
                <Typography variant="body2">
                  <span style={{ color: "#374151" }}>Area Type:</span> {row?.target_area_type}
                </Typography>
                <Typography variant="body2">
                  <span style={{ color: "#374151" }}>Area Name:</span> {getAreaName()}
                </Typography>
                <Typography variant="body2">
                  <span style={{ color: "#374151" }}>VLM:</span> {row?.vlm_done_count}/{row?.vlm_count}
                </Typography>
                <Typography variant="body2">
                  <span style={{ color: "#374151" }}>MVLM:</span> {row?.mvlm_done_count}/{row?.mvlm_count}
                </Typography>
                <Typography variant="body2">
                  <span style={{ color: "#374151" }}>Farmer Target:</span> {row?.farmers_attended || 0}/{row?.farmers_required || 0}
                </Typography>
              </Box>
            </Box>
          ) : (
            <>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                Activity Information
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={4}>
                  <Typography variant="body2" color="textSecondary">Activity ID</Typography>
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>{row?.id}</Typography>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <Typography variant="body2" color="textSecondary">Season</Typography>
                  <Typography variant="body1" sx={{ fontWeight: 400 }}>{seasonDisplay}</Typography>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <Typography variant="body2" color="textSecondary">Target Area Type</Typography>
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>{row?.target_area_type}</Typography>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <Typography variant="body2" color="textSecondary">Target Area Name</Typography>
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>{getAreaName()}</Typography>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <Typography variant="body2" color="textSecondary">VLM</Typography>
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>{row?.vlm_done_count}/{row?.vlm_count}</Typography>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <Typography variant="body2" color="textSecondary">MVLM</Typography>
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>{row?.mvlm_done_count}/{row?.mvlm_count}</Typography>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <Typography variant="body2" color="textSecondary">FARMER TARGET</Typography>
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>{row?.farmers_attended || 0}/{row?.farmers_required || 0}</Typography>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <Typography variant="body2" color="textSecondary">Status</Typography>
                  <Chip
                    label={row?.is_active ? "Active" : "Inactive"}
                    size="small"
                    sx={{
                      mt: 0.5,
                      backgroundColor: row?.is_active ? '#D1FAE5' : '#FECACA',
                      color: row?.is_active ? '#047857' : '#B91C1C',
                      fontWeight: 600
                    }}
                  />
                </Grid>
              </Grid>
            </>
          )}
        </Paper>

        {/* Monthly Targets Section */}
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
          Monthly Targets
        </Typography>
        {filteredMonthlyTargets.length > 0 ? (
          <Box>
            {filteredMonthlyTargets.map((target) => {
              const activities = getActivitiesForMonth(target.id);
              const isExpanded = expandedMonthId === target.id;
              // Support both camelCase and snake_case for VLM/MVLM values
              const vlmCount = target.vlmCount ?? target.vlm_count ?? 0;
              const vlmDoneCount = target.vlmDoneCount ?? target.vlm_done_count ?? 0;
              const mvlmCount = target.mvlmCount ?? target.mvlm_count ?? 0;
              const mvlmDoneCount = target.mvlmDoneCount ?? target.mvlm_done_count ?? 0;
              return (
                <Paper
                  key={target.id}
                  elevation={0}
                  sx={{
                    mb: 2,
                    borderRadius: 3,
                    border: "1px solid #E5E7EB",
                    overflow: "hidden"
                  }}
                >
                  {/* Monthly Target Row - Responsive */}
                  {isXs ? (
                    <Box sx={{ p: 1, minWidth: 200, minHeight: 120, position: "relative" }}>
                      <Chip
                        label={target.is_active || target.isActive ? "Active" : "Inactive"}
                        size="small"
                        sx={{
                          position: "absolute",
                          top: 10,
                          right: 10,
                          backgroundColor: (target.is_active || target.isActive) ? '#D1FAE5' : '#FECACA',
                          color: (target.is_active || target.isActive) ? '#047857' : '#B91C1C',
                          fontWeight: 600,
                          fontSize: 11,
                        }}
                      />
                      <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, mt: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          <span style={{ color: "#374151" }}>Month:</span> {target.month_year || target.monthYear}
                        </Typography>
                        <Typography variant="body2">
                          <span style={{ color: "#374151" }}>VLM:</span> {vlmDoneCount}/{vlmCount}
                        </Typography>
                        <Typography variant="body2">
                          <span style={{ color: "#374151" }}>MVLM:</span> {mvlmDoneCount}/{mvlmCount}
                        </Typography>
                      </Box>
                      {activities.length > 0 && (
                        <Button
                          variant="outlined"
                          color="primary"
                          size="small"
                          sx={{ borderRadius: 2, fontSize: 11, py: 0.3, px: 1, position: "absolute", bottom: 10, right: 10 }}
                          onClick={() => handleToggleActivities(target.id)}
                        >
                          {isExpanded ? "Hide" : "View"} Activities
                        </Button>
                      )}
                    </Box>
                  ) : (
                    <Box sx={{ p: 2, display: "flex", alignItems: "center", gap: 2 }}>
                      <Box sx={{ flex: 1, display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 2, alignItems: "center" }}>
                        <Box>
                          <Typography variant="caption" color="textSecondary">Month ID</Typography>
                          <Typography variant="body2" fontWeight={600}>{target.id}</Typography>
                        </Box>
                        <Box>
                          <Typography variant="caption" color="textSecondary">Month</Typography>
                          <Typography variant="body2" fontWeight={600}>{target.month_year || target.monthYear}</Typography>
                        </Box>
                        <Box>
                          <Typography variant="caption" color="textSecondary">VLM</Typography>
                          <Typography variant="body2" fontWeight={600}>{vlmDoneCount}/{vlmCount}</Typography>
                        </Box>
                        <Box>
                          <Typography variant="caption" color="textSecondary">MVLM</Typography>
                          <Typography variant="body2" fontWeight={600}>{mvlmDoneCount}/{mvlmCount}</Typography>
                        </Box>
                      </Box>
                      {activities.length > 0 && (
                        <Button
                          variant="outlined"
                          color="primary"
                          size="small"
                          sx={{ borderRadius: 2, fontSize: 11, py: 0.4, px: 1.5, whiteSpace: "nowrap" }}
                          onClick={() => handleToggleActivities(target.id)}
                        >
                          {isExpanded ? "Hide" : "View"} Activities
                        </Button>
                      )}
                    </Box>
                  )}

                  {/* Activities Table - Collapsible */}
                  <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                    <Box sx={{ px: 2, pb: 2 }}>
                      <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: "#4B5563" }}>
                        Associated Activities
                      </Typography>
                      {activities.length > 0 ? (
                        isXs ? (
                          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                            {activities.map((activity) => (
                              <Paper
                                key={activity.id}
                                elevation={0}
                                sx={{
                                  border: "1px solid #E5E7EB",
                                  borderRadius: 2,
                                  p: 1.5,
                                  position: "relative",
                                  minWidth: 200,
                                  minHeight: 110,
                                }}
                              >
                                <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                    <span style={{ color: "#374151" }}>Activity Name:</span> {activity.activityName || activity.activity_name}
                                  </Typography>
                                  <Typography variant="body2">
                                    <span style={{ color: "#374151" }}>Village:</span> {activity?.village?.name}
                                  </Typography>
                                  <Typography variant="body2">
                                    <span style={{ color: "#374151" }}>Start Time:</span> {new Date(activity.startTime || activity.start_time).toLocaleString('en-US', {
                                      year: 'numeric',
                                      month: 'short',
                                      day: 'numeric',
                                      hour: '2-digit',
                                      minute: '2-digit'
                                    })}
                                  </Typography>
                                  <Typography variant="body2">
                                    <span style={{ color: "#374151" }}>End Time:</span> {new Date(activity.endTime || activity.end_time).toLocaleString('en-US', {
                                      year: 'numeric',
                                      month: 'short',
                                      day: 'numeric',
                                      hour: '2-digit',
                                      minute: '2-digit'
                                    })}
                                  </Typography>
                                  <Typography variant="body2">
                                    <span style={{ color: "#374151" }}>Farmers:</span> {activity.noOfFarmers || activity.no_of_farmers}
                                  </Typography>
                                </Box>
                                <IconButton
                                  color="primary"
                                  size="small"
                                  onClick={() => handleOpenImages(activity.id)}
                                  sx={{ position: "absolute", bottom: 8, right: 8 }}
                                >
                                  <ImageIcon fontSize="small" />
                                </IconButton>
                              </Paper>
                            ))}
                          </Box>
                        ) : (
                          <Paper elevation={0} sx={{ border: "1px solid #E5E7EB", borderRadius: 2 }}>
                            <Table size="small">
                              <TableHead sx={{ backgroundColor: "#F9FAFB" }}>
                                <TableRow>
                                  <TableCell sx={{ fontWeight: 600, fontSize: 12 }}>Activity ID</TableCell>
                                  <TableCell sx={{ fontWeight: 600, fontSize: 12 }}>Village</TableCell>
                                  <TableCell sx={{ fontWeight: 600, fontSize: 12 }}>Activity Name</TableCell>
                                  <TableCell sx={{ fontWeight: 600, fontSize: 12 }}>Start Time</TableCell>
                                  <TableCell sx={{ fontWeight: 600, fontSize: 12 }}>End Time</TableCell>
                                  <TableCell sx={{ fontWeight: 600, fontSize: 12 }}>No. of Farmers</TableCell>
                                  <TableCell sx={{ fontWeight: 600, fontSize: 12 }}>Actions</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {activities.map((activity) => (
                                  <TableRow key={activity.id}>
                                    <TableCell fontSize="small">{activity.id}</TableCell>
                                    <TableCell fontSize="small">{activity?.village?.name}</TableCell>
                                    <TableCell fontSize="small">{activity.activityName || activity.activity_name}</TableCell>
                                    <TableCell fontSize="small">
                                      {new Date(activity.startTime || activity.start_time).toLocaleString('en-US', {
                                        year: 'numeric',
                                        month: 'short',
                                        day: 'numeric',
                                        hour: '2-digit',
                                        minute: '2-digit'
                                      })}
                                    </TableCell>
                                    <TableCell fontSize="small">
                                      {new Date(activity.endTime || activity.end_time).toLocaleString('en-US', {
                                        year: 'numeric',
                                        month: 'short',
                                        day: 'numeric',
                                        hour: '2-digit',
                                        minute: '2-digit'
                                      })}
                                    </TableCell>
                                    <TableCell fontSize="small">{activity.noOfFarmers || activity.no_of_farmers}</TableCell>
                                    <TableCell>
                                      <IconButton
                                        color="primary"
                                        size="small"
                                        onClick={() => handleOpenImages(activity.id)}
                                      >
                                        <ImageIcon fontSize="small" />
                                      </IconButton>
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </Paper>
                        )
                      ) : (
                        <Typography variant="body2" color="textSecondary" sx={{ py: 2, textAlign: "center" }}>
                          No activities found.
                        </Typography>
                      )}
                    </Box>
                  </Collapse>
                </Paper>
              );
            })}
          </Box>
        ) : (
          <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: "1px solid #E5E7EB", textAlign: "center" }}>
            <Typography color="textSecondary">No monthly targets found for this pre-season activity.</Typography>
          </Paper>
        )}

        {/* Image Dialog */}
        <Dialog open={openImageDialog} onClose={handleCloseImages} maxWidth="md" fullWidth>
          <DialogTitle>Activity Images</DialogTitle>
          <DialogContent dividers>
            {mediaFilesData && Array.isArray(mediaFilesData.data?.media) && mediaFilesData.data.media.length > 0 ? (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                {mediaFilesData.data.media.map((img, idx) => (
                  <Box
                    key={img.id || idx}
                    sx={{ width: 180, height: 180, borderRadius: 2, overflow: 'hidden', border: '1px solid #eee', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#fafafa', cursor: 'pointer' }}
                    onClick={() => window.open(img.filePath, '_blank', 'noopener,noreferrer')}
                  >
                    <img
                      src={img.filePath}
                      alt={img.activity?.activity_name || `Activity Image ${idx + 1}`}
                      style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                    />
                  </Box>
                ))}
              </Box>
            ) : (
              <Typography color="textSecondary">No images found for this activity.</Typography>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseImages} color="primary" variant="contained">Close</Button>
          </DialogActions>
        </Dialog>
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose} color="primary" variant="contained">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PreActivityDetailsView;
